import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/contexts/LangContext";
import { api, apiError } from "@/lib/api";
import { PROPERTY_TYPES, formatPrice, AMMAN_CENTER } from "@/lib/constants";
import { typeLabel } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import LocationPicker from "@/components/LocationPicker";
import MapPicker from "@/components/MapPicker";
import PropertyCard from "@/components/PropertyCard";
import { Sparkles, Search, LineChart, Loader2, ArrowLeft, ArrowRight, ShieldCheck, Info, Home as HomeIcon } from "lucide-react";
import { toast } from "sonner";

const CONFIDENCE = {
  high:         { color: "bg-emerald-100 text-emerald-800 border-emerald-300",   dot: "bg-emerald-500", ar: "🟢 عالية", en: "🟢 High" },
  medium:       { color: "bg-amber-100 text-amber-800 border-amber-300",         dot: "bg-amber-500",   ar: "🟡 جيدة", en: "🟡 Medium" },
  low:          { color: "bg-orange-100 text-orange-800 border-orange-300",      dot: "bg-orange-500",  ar: "🟠 محدودة", en: "🟠 Low" },
  insufficient: { color: "bg-slate-100 text-slate-700 border-slate-300",         dot: "bg-slate-400",   ar: "⚪ غير كافية", en: "⚪ Insufficient" },
};

const PRICE_ANALYSIS = {
  below_market: { ar: "السعر المطلوب أقل من نطاق التقييم — فرصة جيدة للمشتري.", en: "Asking price is below the estimated range." },
  in_range:     { ar: "السعر المطلوب ضمن نطاق التقييم السوقي.", en: "Asking price is within the estimated range." },
  above_market: { ar: "السعر المطلوب أعلى من نطاق التقييم — قد يحتاج مراجعة.", en: "Asking price is above the estimated range." },
};

function ConfidenceBadge({ level, lang }) {
  const c = CONFIDENCE[level] || CONFIDENCE.insufficient;
  return (
    <span className={`inline-flex items-center gap-2 text-xs font-bold px-3 py-1.5 rounded-full border ${c.color}`}>
      <span className={`w-2 h-2 rounded-full ${c.dot}`} /> {lang === "ar" ? c.ar : c.en}
    </span>
  );
}

function AiHeader({ lang }) {
  return (
    <div className="text-center max-w-3xl mx-auto mb-10">
      <div className="inline-flex items-center gap-2 text-xs font-bold text-[#062C22] bg-[#EBDDA6] px-3 py-1.5 rounded-full mb-4">
        <Sparkles className="w-3.5 h-3.5" /> {lang === "ar" ? "🤖 ذكاء فلوريت العقاري" : "🤖 FLUORIT AI"}
      </div>
      <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-emerald-950 leading-[1.15]">
        {lang === "ar" ? "ابحث عن عقارك… أو اكتشف قيمته" : "Find your property… or discover its value"}
      </h1>
      <p className="mt-3 text-slate-700 text-base sm:text-lg">
        {lang === "ar"
          ? "مساعد عقاري ذكي يساعدك في البحث عن العقارات وتحليل الفرص والحصول على تقدير استرشادي لقيمة عقارك."
          : "A smart real-estate assistant to search properties, analyze opportunities and get an indicative valuation."}
      </p>
    </div>
  );
}

function Tabs({ tab, setTab, lang }) {
  const tabs = [
    { key: "search", ar: "🔎 بحث ذكي عن العقارات", en: "🔎 Smart property search" },
    { key: "valuation", ar: "📊 قيّم عقارك", en: "📊 Value your property" },
  ];
  return (
    <div className="flex items-center justify-center mb-8">
      <div className="inline-flex bg-white border border-emerald-900/10 rounded-full p-1 shadow-sm">
        {tabs.map((t) => (
          <button key={t.key} data-testid={`ai-tab-${t.key}`} onClick={() => setTab(t.key)}
                  className={`px-4 sm:px-5 py-2 text-sm font-bold rounded-full transition ${
                    tab === t.key ? "bg-emerald-900 text-white shadow" : "text-emerald-950 hover:bg-emerald-50"
                  }`}>
            {lang === "ar" ? t.ar : t.en}
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------- SMART SEARCH ----------
function SmartSearch() {
  const { lang, isRTL } = useLang();
  const nav = useNavigate();
  const Arrow = isRTL ? ArrowLeft : ArrowRight;
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const examples = lang === "ar" ? [
    "أرض سكنية في عمّان مساحتها 700 متر وأقل من 150 ألف",
    "شقة في إربد 3 غرف نوم بسعر أقل من 60 ألف",
    "فيلا في عبدون مع مسبح", "عمارة استثمارية في الزرقاء بأقل من 250 ألف",
  ] : [
    "residential land in Amman around 700 sqm under 150k",
    "3-bedroom apartment in Irbid under 60k",
    "villa in Abdoun with pool", "investment building in Zarqa under 250k",
  ];

  const doSearch = async (query) => {
    const text = (query ?? q).trim();
    if (text.length < 3) { toast.error(lang === "ar" ? "اكتب طلبك بوضوح" : "Please describe what you want"); return; }
    setLoading(true); setErr(""); setData(null);
    try {
      const { data } = await api.post("/ai/property-search", { query: text, limit: 12 });
      setData(data);
    } catch (e) { setErr(apiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-5xl mx-auto">
      <div className="bg-white rounded-2xl border border-emerald-900/10 shadow-[0_20px_60px_-30px_rgba(6,44,34,.25)] p-5 sm:p-6">
        <Label className="text-emerald-950 font-bold text-sm">
          {lang === "ar" ? "اكتب طلبك بلغتك الطبيعية" : "Describe what you're looking for"}
        </Label>
        <Textarea data-testid="ai-search-input" value={q} onChange={(e) => setQ(e.target.value)}
                  rows={3} placeholder={examples[0]}
                  className="mt-2 text-base resize-none" />
        <div className="mt-3 flex flex-wrap gap-2">
          {examples.map((ex, i) => (
            <button key={i} onClick={() => { setQ(ex); doSearch(ex); }}
                    className="text-xs bg-emerald-50 hover:bg-emerald-100 text-emerald-900 border border-emerald-200 rounded-full px-3 py-1.5">
              {ex}
            </button>
          ))}
        </div>
        <div className="mt-4 flex justify-end">
          <Button data-testid="ai-search-btn" onClick={() => doSearch()} disabled={loading}
                  className="bg-emerald-900 hover:bg-emerald-950 text-white h-11 px-6 gap-2">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            {lang === "ar" ? "ابحث بالذكاء الاصطناعي" : "Search with AI"}
          </Button>
        </div>
      </div>

      {err && <div className="mt-6 text-sm bg-rose-50 border border-rose-200 text-rose-800 rounded-xl px-4 py-3">{err}</div>}

      {data && (
        <div className="mt-8">
          {Object.keys(data.filters || {}).length > 0 && (
            <div className="mb-4 flex flex-wrap gap-2" data-testid="ai-filters">
              {Object.entries(data.filters).map(([k, v]) => (
                <span key={k} className="text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-full px-3 py-1">
                  <b>{k}:</b> {String(v)}
                </span>
              ))}
            </div>
          )}
          {data.summary && (
            <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-emerald-950 text-sm leading-relaxed">
              <div className="font-bold mb-1 inline-flex items-center gap-2"><Sparkles className="w-4 h-4 text-amber-600" /> {lang === "ar" ? "تحليل المساعد" : "AI insight"}</div>
              {data.summary}
              <div className="mt-3 text-[11px] text-emerald-900/70 border-t border-emerald-200 pt-2">
                معلومات استرشادية فقط. راجع <a href="/disclaimer" target="_blank" rel="noreferrer" className="font-semibold hover:text-amber-700 underline">إخلاء المسؤولية</a>.
              </div>
            </div>
          )}
          {data.items.length === 0 ? (
            <div className="text-center bg-white rounded-2xl border border-slate-200 py-10 text-slate-600">
              {lang === "ar" ? "لم نجد عقارات مطابقة. جرّب توسيع نطاق البحث." : "No matches. Try broadening your query."}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="ai-search-results">
              {data.items.map((p) => <PropertyCard key={p.id} property={p} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------- VALUATION ----------
const CLASSIFICATIONS = ["سكني", "تجاري", "صناعي", "زراعي", "استثماري", "خدمي", "غير مصنف"];
const CONDITIONS = ["ممتاز", "جيد جداً", "جيد", "متوسط", "بحاجة تجديد"];

function Valuation() {
  const { lang, isRTL } = useLang();
  const nav = useNavigate();
  const Arrow = isRTL ? ArrowLeft : ArrowRight;
  const [f, setF] = useState({
    type: "", listing_type: "sale",
    governorate: "", city: "", area: "",
    village: "", basin: "", parcel_number: "",
    area_sqm: "", classification: "", streets_count: "",
    street_width: "", frontage: "", asking_price: "",
    bedrooms: "", building_age: "", condition: "", notes: "",
    lat: null, lng: null,
  });
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    if (!f.type) { toast.error(lang === "ar" ? "اختر نوع العقار" : "Choose property type"); return; }
    if (!f.area_sqm || parseFloat(f.area_sqm) <= 0) { toast.error(lang === "ar" ? "أدخل المساحة" : "Enter area"); return; }
    setLoading(true); setErr(""); setRes(null);
    try {
      const payload = {
        type: f.type, listing_type: f.listing_type,
        governorate: f.governorate || "", city: f.city || "", area: f.area || "",
        village: f.village || "", basin: f.basin || "", parcel_number: f.parcel_number || "",
        area_sqm: parseFloat(f.area_sqm),
        classification: f.classification || "",
        streets_count: f.streets_count ? parseInt(f.streets_count) : null,
        street_width: f.street_width ? parseFloat(f.street_width) : null,
        frontage: f.frontage ? parseFloat(f.frontage) : null,
        asking_price: f.asking_price ? parseFloat(f.asking_price) : null,
        bedrooms: f.bedrooms ? parseInt(f.bedrooms) : null,
        building_age: f.building_age ? parseInt(f.building_age) : null,
        condition: f.condition || "", notes: f.notes || "",
        lat: f.lat, lng: f.lng,
      };
      const { data } = await api.post("/ai/property-valuation", payload);
      setRes(data);
      setTimeout(() => document.getElementById("val-report")?.scrollIntoView({ behavior: "smooth" }), 200);
    } catch (e) { setErr(apiError(e)); }
    finally { setLoading(false); }
  };

  const publishFree = () => {
    // Map area_sqm into the correct details key by type
    const detailKeyByType = { land: "area_sqm", apartment: "area_sqm", commercial: "area_sqm", other: "area_sqm",
                              villa: "land_area", building: "land_area", industrial: "land_area", farm: "land_area", hotel: "land_area" };
    const areaKey = detailKeyByType[f.type] || "area_sqm";
    const details = {};
    if (f.area_sqm) details[areaKey] = parseFloat(f.area_sqm);
    if (f.bedrooms) details.bedrooms = parseInt(f.bedrooms);
    if (f.frontage) details.frontage = parseFloat(f.frontage);
    if (f.streets_count) details.streets = parseInt(f.streets_count);
    if (f.classification) details.zoning = f.classification;
    if (f.building_age) details.age = parseInt(f.building_age);
    if (f.notes) details.notes = f.notes;
    const prefill = {
      type: f.type, governorate: f.governorate, city: f.city, area: f.area,
      lat: f.lat, lng: f.lng,
      asking_price: f.asking_price ? parseFloat(f.asking_price) : null,
      details,
    };
    sessionStorage.setItem("fluorit_ai_prefill", JSON.stringify(prefill));
    nav("/sell");
  };

  return (
    <div className="max-w-5xl mx-auto">
      <div className="bg-white rounded-2xl border border-emerald-900/10 shadow-[0_20px_60px_-30px_rgba(6,44,34,.25)] p-5 sm:p-6 space-y-6">
        {/* Basics */}
        <div>
          <h3 className="font-bold text-emerald-950 mb-3">{lang === "ar" ? "1) بيانات أساسية" : "1) Basics"}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label>{lang === "ar" ? "نوع العقار" : "Property type"} *</Label>
              <Select value={f.type} onValueChange={(v) => set("type", v)}>
                <SelectTrigger data-testid="val-type" className="mt-1"><SelectValue placeholder={lang === "ar" ? "اختر" : "Select"} /></SelectTrigger>
                <SelectContent>
                  {PROPERTY_TYPES.map((x) => <SelectItem key={x.key} value={x.key}>{lang === "ar" ? x.ar : x.en}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{lang === "ar" ? "نوع الإدراج" : "Listing"}</Label>
              <Select value={f.listing_type} onValueChange={(v) => set("listing_type", v)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sale">{lang === "ar" ? "بيع" : "Sale"}</SelectItem>
                  <SelectItem value="rent">{lang === "ar" ? "إيجار" : "Rent"}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{lang === "ar" ? "السعر المطلوب (د.أ)" : "Asking price (JOD)"}</Label>
              <Input data-testid="val-asking-price" type="number" className="mt-1" value={f.asking_price} onChange={(e) => set("asking_price", e.target.value)} />
            </div>
            <div className="sm:col-span-3">
              <LocationPicker city={f.city} area={f.area}
                              onChange={(loc) => setF((s) => ({ ...s, ...loc, governorate: loc.city || s.governorate }))}
                              allowAll={false} creatableArea={true} />
            </div>
            <Input placeholder={lang === "ar" ? "القرية" : "Village"} value={f.village} onChange={(e) => set("village", e.target.value)} />
            <Input placeholder={lang === "ar" ? "الحوض" : "Basin"} value={f.basin} onChange={(e) => set("basin", e.target.value)} />
            <Input placeholder={lang === "ar" ? "رقم القطعة" : "Parcel #"} value={f.parcel_number} onChange={(e) => set("parcel_number", e.target.value)} />
          </div>
        </div>

        {/* Specs */}
        <div>
          <h3 className="font-bold text-emerald-950 mb-3">{lang === "ar" ? "2) مواصفات العقار" : "2) Property specs"}</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <Label>{lang === "ar" ? "المساحة (م²)" : "Area (sqm)"} *</Label>
              <Input data-testid="val-area" type="number" className="mt-1" value={f.area_sqm} onChange={(e) => set("area_sqm", e.target.value)} />
            </div>
            <div>
              <Label>{lang === "ar" ? "التصنيف" : "Classification"}</Label>
              <Select value={f.classification} onValueChange={(v) => set("classification", v)}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{CLASSIFICATIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>{lang === "ar" ? "عدد الشوارع" : "Streets"}</Label>
              <Input type="number" className="mt-1" value={f.streets_count} onChange={(e) => set("streets_count", e.target.value)} />
            </div>
            <div>
              <Label>{lang === "ar" ? "عرض الشارع (م)" : "Street width (m)"}</Label>
              <Input type="number" className="mt-1" value={f.street_width} onChange={(e) => set("street_width", e.target.value)} />
            </div>
            <div>
              <Label>{lang === "ar" ? "الواجهة (م)" : "Frontage (m)"}</Label>
              <Input type="number" className="mt-1" value={f.frontage} onChange={(e) => set("frontage", e.target.value)} />
            </div>
            <div>
              <Label>{lang === "ar" ? "عدد الغرف" : "Bedrooms"}</Label>
              <Input type="number" className="mt-1" value={f.bedrooms} onChange={(e) => set("bedrooms", e.target.value)} />
            </div>
            <div>
              <Label>{lang === "ar" ? "عمر البناء (سنة)" : "Building age (yrs)"}</Label>
              <Input type="number" className="mt-1" value={f.building_age} onChange={(e) => set("building_age", e.target.value)} />
            </div>
            <div>
              <Label>{lang === "ar" ? "الحالة" : "Condition"}</Label>
              <Select value={f.condition} onValueChange={(v) => set("condition", v)}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{CONDITIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-3">
            <Label>{lang === "ar" ? "معلومات إضافية" : "Additional notes"}</Label>
            <Textarea rows={3} className="mt-1 resize-none" value={f.notes} onChange={(e) => set("notes", e.target.value)} />
          </div>
        </div>

        {/* Map */}
        <div>
          <h3 className="font-bold text-emerald-950 mb-3">{lang === "ar" ? "3) موقع العقار على الخريطة (اختياري)" : "3) Location on map (optional)"}</h3>
          <MapPicker value={f.lat && f.lng ? { lat: f.lat, lng: f.lng } : null}
                     onChange={(p) => setF((s) => ({ ...s, lat: p.lat, lng: p.lng }))}
                     height={280} />
          {f.lat && <div className="text-xs text-slate-500 mt-1 fl-num">{f.lat.toFixed(5)}, {f.lng.toFixed(5)}</div>}
        </div>

        <div className="flex justify-end">
          <Button data-testid="val-submit" onClick={submit} disabled={loading}
                  className="bg-emerald-900 hover:bg-emerald-950 text-white h-11 px-7 gap-2">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <LineChart className="w-4 h-4" />}
            {lang === "ar" ? "احصل على التقييم" : "Get valuation"}
          </Button>
        </div>
      </div>

      {err && <div className="mt-6 text-sm bg-rose-50 border border-rose-200 text-rose-800 rounded-xl px-4 py-3">{err}</div>}

      {res && (
        <div id="val-report" className="mt-8 space-y-5" data-testid="val-report">
          {/* Header card */}
          <div className="bg-gradient-to-br from-emerald-900 to-[#0D5C46] text-white rounded-2xl p-6 sm:p-8 shadow-[0_30px_80px_-40px_rgba(6,44,34,.6)]">
            <div className="flex items-center gap-2 text-xs text-[#EBDDA6] font-bold uppercase tracking-wider mb-2">
              <ShieldCheck className="w-4 h-4" /> {lang === "ar" ? "تقرير فلوريت الذكي" : "FLUORIT smart report"}
            </div>
            {res.confidence === "insufficient" ? (
              <div>
                <div className="text-lg font-bold">{lang === "ar" ? "بيانات مقارنة غير كافية" : "Insufficient comparable data"}</div>
                <p className="text-white/85 mt-2 text-sm">{lang === "ar" ? res.explanation_ar : res.explanation_en}</p>
              </div>
            ) : (
              <>
                <div className="text-xs text-white/70">{lang === "ar" ? "التقييم السوقي التقديري" : "Indicative market range"}</div>
                <div className="text-4xl sm:text-5xl font-extrabold mt-1 fl-num">
                  {formatPrice(res.estimate_min)} – {formatPrice(res.estimate_max)}
                  <span className="text-lg font-bold text-[#EBDDA6] ms-2">{lang === "ar" ? "د.أ" : "JOD"}</span>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <ConfidenceBadge level={res.confidence} lang={lang} />
                  {res.price_per_sqm_avg && (
                    <span className="text-xs text-white/85">
                      {lang === "ar" ? "متوسط سعر المتر:" : "Avg price / sqm:"} <b className="fl-num">{res.price_per_sqm_avg}</b> د.أ
                    </span>
                  )}
                  <span className="text-xs text-white/85">{lang === "ar" ? `${res.comparables_count} عقارات مشابهة` : `${res.comparables_count} comparables`}</span>
                </div>
                {res.price_analysis && f.asking_price && (
                  <div className="mt-4 bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-sm">
                    {PRICE_ANALYSIS[res.price_analysis][lang === "ar" ? "ar" : "en"]}
                  </div>
                )}
              </>
            )}
          </div>

          {/* AI reasoning */}
          {res.explanation_ar && res.confidence !== "insufficient" && (
            <div className="bg-white border border-emerald-900/10 rounded-2xl p-5">
              <div className="font-bold text-emerald-950 inline-flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-amber-600" /> {lang === "ar" ? "شرح المساعد الذكي" : "AI reasoning"}
              </div>
              <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{res.explanation_ar}</p>
            </div>
          )}

          {/* Administrative value note */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
            <div className="font-bold text-amber-900 inline-flex items-center gap-2 mb-1">🏛 {lang === "ar" ? "القيمة الإدارية الرسمية" : "Official administrative value"}</div>
            {res.administrative_value ? (
              <div className="text-sm text-amber-950">
                <span className="fl-num font-bold text-lg">{formatPrice(res.administrative_value)} د.أ</span>
                <div className="text-xs text-amber-800 mt-1">
                  {lang === "ar" ? "المصدر:" : "Source:"} {res.administrative_value_source} · {res.administrative_value_date}
                </div>
              </div>
            ) : (
              <p className="text-sm text-amber-950 leading-relaxed">{res.administrative_value_note_ar}</p>
            )}
          </div>

          {/* Comparables */}
          {res.comparables && res.comparables.length > 0 && (
            <div className="bg-white border border-emerald-900/10 rounded-2xl p-5">
              <div className="font-bold text-emerald-950 mb-4">🔎 {lang === "ar" ? `عقارات مشابهة (${res.comparables.length})` : `Comparable properties (${res.comparables.length})`}</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {res.comparables.map((c) => (
                  <a key={c.id} href={`/property/${c.id}`} target="_blank" rel="noreferrer"
                     data-testid={`comp-${c.id}`}
                     className="flex gap-3 bg-emerald-50/50 border border-emerald-900/10 rounded-xl p-3 hover:border-emerald-900 transition">
                    {c.images?.[0] ? (
                      <img src={`/api/files/${c.images[0]}`} alt="" className="w-20 h-20 object-cover rounded-lg" />
                    ) : (
                      <div className="w-20 h-20 bg-emerald-900/10 rounded-lg" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-emerald-950 truncate">{c.title || "—"}</div>
                      <div className="text-xs text-slate-600 truncate">{c.city} · {c.area_name}</div>
                      <div className="mt-1 text-sm font-bold text-emerald-900 fl-num">{formatPrice(c.price)} د.أ</div>
                      <div className="text-[11px] text-slate-500 fl-num">{c.area_sqm} م² · {c.price_per_sqm} د.أ/م²</div>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Publish free CTA */}
          <div className="bg-[#062C22] text-white rounded-2xl p-6 text-center">
            <div className="text-lg sm:text-xl font-bold">{lang === "ar" ? "هل تريد عرض عقارك أمام الباحثين؟" : "Want to list your property?"}</div>
            <p className="text-white/80 text-sm mt-1">{lang === "ar" ? "الإعلان مجاني خلال فترة الإطلاق — سنملأ بيانات عقارك تلقائياً." : "Free during launch — we'll pre-fill your details."}</p>
            <Button data-testid="publish-free-btn" onClick={publishFree}
                    className="mt-4 bg-[#B7962E] hover:bg-[#a3831f] text-[#062C22] font-bold h-12 px-6 gap-2">
              <HomeIcon className="w-4 h-4" /> {lang === "ar" ? "🏡 اعرض عقارك مجاناً" : "🏡 List your property free"}
            </Button>
          </div>

          {/* Disclaimer */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-xs text-slate-600 leading-relaxed inline-flex items-start gap-2">
            <Info className="w-4 h-4 text-slate-500 shrink-0 mt-0.5" />
            <span>{lang === "ar" ? res.disclaimer_ar : res.disclaimer_en}</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- ROOT ----------
export default function AiAssistant() {
  const { lang } = useLang();
  const [tab, setTab] = useState("search");
  return (
    <div className="min-h-[70vh] bg-gradient-to-b from-[#F7F5EC] to-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <AiHeader lang={lang} />
        <Tabs tab={tab} setTab={setTab} lang={lang} />
        {tab === "search" ? <SmartSearch /> : <Valuation />}
      </div>
    </div>
  );
}
